/**
 * Conversation List - Vitreous Support
 * Shows conversations (DMs and groups) with last message preview
 */

import React, { useState } from 'react';
import { Input, Button, Badge, Avatar, Typography, Empty, Modal, Form, Select, Radio, List } from 'antd';
import { SearchOutlined, UserOutlined, WechatOutlined, TeamOutlined, LockOutlined, AppstoreOutlined } from '@ant-design/icons';
import { message as antMessage } from 'antd';
import { useQuery, useQueryClient } from 'react-query';
import api from '../../config/api';
import { useChatWebSocket } from '../../contexts/ChatWebSocketContext';
import { PresenceIndicator } from './PresenceIndicator';
import { useAuth } from '../../contexts/AuthContext';
import dayjs from 'dayjs';
import relativeTime from 'dayjs/plugin/relativeTime';
import isToday from 'dayjs/plugin/isToday';
import isYesterday from 'dayjs/plugin/isYesterday';
import './ConversationList.css';

dayjs.extend(relativeTime);
dayjs.extend(isToday);
dayjs.extend(isYesterday);

const { Text } = Typography;

type PresenceStatus = 'online' | 'away' | 'busy' | 'offline';

const normalizeStatus = (status: string | undefined): PresenceStatus => {
  if (status === 'online' || status === 'away' || status === 'busy' || status === 'offline') return status;
  return 'offline';
};

interface Conversation {
  id: string;
  name: string;
  type: 'group' | 'direct' | 'private';
  unread_count?: number;
  unreadCount?: number;
  last_message_content?: string;
  lastMessageContent?: string;
  last_message_at?: string;
  lastMessageAt?: string;
  other_user?: {
    id: string;
    first_name: string;
    last_name: string;
    email: string;
    presence_status?: string;
  };
  otherUser?: {
    id: string;
    firstName: string;
    lastName: string;
    email: string;
    presenceStatus?: string;
  };
}

interface ConversationListProps {
  selectedConversationId: string | null;
  onSelectConversation: (id: string) => void;
}

export const ConversationList: React.FC<ConversationListProps> = ({
  selectedConversationId,
  onSelectConversation,
}) => {
  const { presence } = useChatWebSocket();
  const { user } = useAuth();
  const [searchQuery, setSearchQuery] = useState('');
  const [newChatVisible, setNewChatVisible] = useState(false);
  const [createChannelVisible, setCreateChannelVisible] = useState(false);
  const [browseChannelsVisible, setBrowseChannelsVisible] = useState(false);
  const [publicSearchQuery, setPublicSearchQuery] = useState('');
  const [creatingChannel, setCreatingChannel] = useState(false);
  const [joiningChannel, setJoiningChannel] = useState<string | null>(null);
  const [newChatForm] = Form.useForm();
  const [createChannelForm] = Form.useForm();
  const queryClient = useQueryClient();

  const { data: channelsData, isLoading } = useQuery(
    'chat-channels',
    () => api.get('/api/chat/channels').then(res => res.data),
    { refetchInterval: 10000 }
  );

  const { data: usersData } = useQuery(
    'chat-users',
    () => api.get('/api/chat/users').then(res => res.data),
    { enabled: newChatVisible }
  );

  const { data: publicChannelsData, isLoading: loadingPublicChannels, refetch: refetchPublicChannels } = useQuery(
    ['public-channels', publicSearchQuery],
    () => {
      const searchParam = publicSearchQuery.trim() ? `?search=${encodeURIComponent(publicSearchQuery.trim())}` : '';
      return api.get(`/api/chat/channels/public${searchParam}`).then(res => res.data);
    },
    { enabled: browseChannelsVisible, refetchOnWindowFocus: false }
  );

  const conversations: Conversation[] = channelsData?.channels || [];

  const filteredConversations = conversations.filter(c => {
    if (!searchQuery) return true;
    const q = searchQuery.toLowerCase();
    if (c.type === 'direct') {
      const other = c.other_user || c.otherUser;
      if (other) {
        const fn = (other as any).first_name || (other as any).firstName || '';
        const ln = (other as any).last_name || (other as any).lastName || '';
        return `${fn} ${ln}`.toLowerCase().includes(q) || (other.email || '').toLowerCase().includes(q);
      }
    }
    return (c.name || '').toLowerCase().includes(q);
  });

  // Sort by last message time
  const sortedConversations = [...filteredConversations].sort((a, b) => {
    const aTime = a.last_message_at || a.lastMessageAt || (a as any).created_at || (a as any).createdAt || '';
    const bTime = b.last_message_at || b.lastMessageAt || (b as any).created_at || (b as any).createdAt || '';
    return bTime.localeCompare(aTime);
  });

  const getDisplayName = (c: Conversation) => {
    if (c.type === 'direct') {
      const other = c.other_user || c.otherUser;
      if (other) {
        const fn = (other as any).first_name || (other as any).firstName || '';
        const ln = (other as any).last_name || (other as any).lastName || '';
        return `${fn} ${ln}`.trim() || other.email || 'Unknown';
      }
    }
    return c.name || 'Unnamed';
  };

  const getLastMessagePreview = (c: Conversation) => {
    const content = c.last_message_content || c.lastMessageContent || '';
    if (!content) return 'No messages yet';
    if (content.length > 45) return content.substring(0, 45) + '...';
    return content;
  };

  const getLastMessageTime = (c: Conversation) => {
    const ts = c.last_message_at || c.lastMessageAt;
    if (!ts) return '';
    const d = dayjs(ts);
    if (d.isToday()) return d.format('h:mm A');
    if (d.isYesterday()) return 'Yesterday';
    if (d.isSame(dayjs(), 'year')) return d.format('MMM D');
    return d.format('MMM D, YY');
  };

  const handleStartChat = async (values: { userId: string }) => {
    try {
      const res = await api.post('/api/chat/channels/direct', { userId: values.userId });
      queryClient.invalidateQueries('chat-channels');
      setNewChatVisible(false);
      newChatForm.resetFields();
      if (res.data?.channel) onSelectConversation(res.data.channel.id);
    } catch (err: any) {
      antMessage.error((err as any)?.response?.data?.error || 'Failed to start chat');
    }
  };

  const handleCreateChannel = async (values: any) => {
    if (creatingChannel) return;
    try {
      setCreatingChannel(true);
      const isPrivate = values.isPrivate === true || values.isPrivate === 'true';
      const res = await api.post('/api/chat/channels', {
        name: values.name?.trim(),
        description: values.description?.trim() || null,
        isPrivate,
      });
      queryClient.invalidateQueries('chat-channels');
      setCreateChannelVisible(false);
      createChannelForm.resetFields();
      antMessage.success('Channel created');
      if (res.data?.channel) onSelectConversation(res.data.channel.id);
    } catch (err: any) {
      antMessage.error((err as any)?.response?.data?.error || 'Failed to create channel');
    } finally {
      setCreatingChannel(false);
    }
  };

  const handleJoinPublicChannel = async (channelId: string) => {
    setJoiningChannel(channelId);
    try {
      await api.post(`/api/chat/channels/${channelId}/join`);
      queryClient.invalidateQueries('chat-channels');
      refetchPublicChannels();
      onSelectConversation(channelId);
      setBrowseChannelsVisible(false);
      antMessage.success('Joined channel');
    } catch (err: any) {
      antMessage.error((err as any)?.response?.data?.error || 'Failed to join channel');
    } finally {
      setJoiningChannel(null);
    }
  };

  return (
    <div className="conversation-list">
      <div className="conversation-list-header">
        <div className="conversation-list-search">
          <SearchOutlined className="search-icon" />
          <Input
            placeholder="Search conversations"
            value={searchQuery}
            onChange={e => setSearchQuery(e.target.value)}
            bordered={false}
            className="search-input"
          />
        </div>
        <div className="conversation-list-actions">
          <Button
            type="text"
            icon={<WechatOutlined />}
            className="new-chat-btn"
            onClick={() => setNewChatVisible(true)}
            title="Direct message"
          />
          <Button
            type="text"
            icon={<TeamOutlined />}
            className="new-chat-btn"
            onClick={() => setCreateChannelVisible(true)}
            title="Create channel"
          />
          <Button
            type="text"
            icon={<AppstoreOutlined />}
            className="new-chat-btn"
            onClick={() => setBrowseChannelsVisible(true)}
            title="Browse public channels"
          />
        </div>
      </div>

      <div className="conversation-list-items">
        {isLoading ? (
          <div className="conversation-list-loading">Loading...</div>
        ) : sortedConversations.length === 0 ? (
          <Empty description="No conversations" className="conversation-list-empty" />
        ) : (
          sortedConversations.map(c => {
            const other = c.other_user || c.otherUser;
            const status = other
              ? normalizeStatus(presence[other.id]?.status || (other as any).presence_status || (other as any).presenceStatus)
              : 'offline';
            const unread = c.unread_count ?? c.unreadCount ?? 0;
            const isSelected = selectedConversationId === c.id;

            return (
              <div
                key={c.id}
                className={`conversation-item ${isSelected ? 'active' : ''}`}
                onClick={() => onSelectConversation(c.id)}
              >
                <div className="conversation-avatar">
                  {c.type === 'direct' && other ? (
                    <PresenceIndicator status={status} size="small">
                      <Avatar size={48} icon={<UserOutlined />} className="avatar" />
                    </PresenceIndicator>
                  ) : (
                    <Avatar size={48} icon={<UserOutlined />} className="avatar group-avatar" />
                  )}
                </div>
                <div className="conversation-info">
                  <div className="conversation-top">
                    <Text strong className="conversation-name" ellipsis>
                      {getDisplayName(c)}
                    </Text>
                    <span className="conversation-time">{getLastMessageTime(c)}</span>
                  </div>
                  <div className="conversation-bottom">
                    <Text className="conversation-preview" ellipsis>
                      {getLastMessagePreview(c)}
                    </Text>
                    {unread > 0 && (
                      <Badge count={unread} className="conversation-unread" />
                    )}
                  </div>
                </div>
              </div>
            );
          })
        )}
      </div>

      <Modal
        title="Direct message"
        open={newChatVisible}
        onCancel={() => {
          setNewChatVisible(false);
          newChatForm.resetFields();
        }}
        footer={null}
      >
        <Form form={newChatForm} onFinish={handleStartChat} layout="vertical">
          <Form.Item name="userId" label="Select contact" rules={[{ required: true }]}>
            <Select
              placeholder="Search users..."
              showSearch
              filterOption={(input, opt) =>
                (opt?.label ?? '').toString().toLowerCase().includes(input.toLowerCase())
              }
              options={(usersData?.users || [])
                .filter((u: any) => u.id !== user?.id)
                .map((u: any) => {
                  const fn = u.first_name || u.firstName || '';
                  const ln = u.last_name || u.lastName || '';
                  return {
                    value: u.id,
                    label: `${fn} ${ln}`.trim() || u.email || 'Unknown',
                  };
                })}
            />
          </Form.Item>
          <Form.Item>
            <Button type="primary" htmlType="submit" block>
              Start chat
            </Button>
          </Form.Item>
        </Form>
      </Modal>

      <Modal
        title="Create channel"
        open={createChannelVisible}
        onCancel={() => {
          setCreateChannelVisible(false);
          createChannelForm.resetFields();
        }}
        footer={null}
      >
        <Form form={createChannelForm} onFinish={handleCreateChannel} layout="vertical">
          <Form.Item name="name" label="Channel name" rules={[{ required: true, message: 'Enter a channel name' }]}>
            <Input placeholder="e.g. general, announcements" />
          </Form.Item>
          <Form.Item name="description" label="Description (optional)">
            <Input.TextArea rows={2} placeholder="What's this channel about?" />
          </Form.Item>
          <Form.Item name="isPrivate" label="Visibility" initialValue={false}>
            <Radio.Group>
              <Radio value={false}>Public – Anyone can join</Radio>
              <Radio value={true}>Private – Only invited members</Radio>
            </Radio.Group>
          </Form.Item>
          <Form.Item>
            <Button type="primary" htmlType="submit" block loading={creatingChannel} disabled={creatingChannel}>
              Create channel
            </Button>
          </Form.Item>
        </Form>
      </Modal>

      <Modal
        title="Browse public channels"
        open={browseChannelsVisible}
        onCancel={() => {
          setBrowseChannelsVisible(false);
          setPublicSearchQuery('');
        }}
        footer={null}
        width={560}
      >
        <Input
          placeholder="Search channels..."
          prefix={<SearchOutlined />}
          value={publicSearchQuery}
          onChange={e => setPublicSearchQuery(e.target.value)}
          style={{ marginBottom: 16 }}
        />
        {loadingPublicChannels ? (
          <div style={{ textAlign: 'center', padding: 40 }}>Loading...</div>
        ) : (
          <List
            dataSource={publicChannelsData?.channels || []}
            renderItem={(ch: any) => (
              <List.Item
                actions={[
                  <Button
                    key="join"
                    type="primary"
                    size="small"
                    loading={joiningChannel === ch.id}
                    onClick={() => handleJoinPublicChannel(ch.id)}
                    disabled={ch.is_member}
                  >
                    {ch.is_member ? 'Joined' : 'Join'}
                  </Button>,
                ]}
              >
                <List.Item.Meta
                  title={
                    <span style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                      {ch.is_private ? <LockOutlined style={{ color: '#aab3c5' }} /> : <TeamOutlined style={{ color: '#5856d6' }} />}
                      <Text strong>{ch.name}</Text>
                      {ch.is_private && <Typography.Text type="secondary">(Private)</Typography.Text>}
                    </span>
                  }
                  description={
                    <span>
                      {ch.description && <Typography.Text type="secondary">{ch.description}</Typography.Text>}
                      <Typography.Text type="secondary" style={{ display: 'block', fontSize: 12, marginTop: 4 }}>
                        {ch.member_count ?? 0} members
                      </Typography.Text>
                    </span>
                  }
                />
              </List.Item>
            )}
          />
        )}
        {!loadingPublicChannels && (!publicChannelsData?.channels || publicChannelsData.channels.length === 0) && (
          <Empty description="No public channels found" style={{ marginTop: 40 }} />
        )}
      </Modal>
    </div>
  );
};
